<template>
	<HeaderWrapper>
		<div
			v-if="loading"
			class="flex items-start gap-2"
		>
			{{ t('newSession.loadingPreviousPlans') }}
			<LoadingSVG class="h-4! w-4!" />
		</div>
		<div
			v-else
			@click="showSelectionModal = true"
			aria-label="showPlanSelection"
			class="cursor-pointer"
		>
			<HeaderText :selected-plan-option="selectedPlanOption" />
		</div>
		<div v-if="selectedPlanOption?.lastUpdated">
			<!-- this button should start a new plan -->
			<button
				@click.prevent="startNewPlan"
				class="align-text-top text-xs text-slate-500 italic underline"
			>
				{{ t('newSession.startNew') }}
			</button>
		</div>
	</HeaderWrapper>

	<PrecoaModal
		class="sm:w-[30rem]! sm:max-w-none"
		isModalForValueInput
		v-model:open="showSelectionModal"
		@close="showSelectionModal = false"
	>
		<SelectPlanModalContent
			:plans="importedPlanOptions"
			:initialSelection="selectedPlanOption"
			:selectedPriceBook="selectedPriceBook"
			@planSelected="handleSelectedPlanChange"
			@cancel="showSelectionModal = false"
		/>
	</PrecoaModal>
</template>

<script setup lang="ts">
import type { PriceBook } from '@/classes/PriceBook'
import LoadingSVG from '@/components/common/LoadingSVG.vue'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import { usePlanVersionsV2 } from '@/mixins/planVersionsV2'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { usePlanStore } from '@/stores/plan'
import type { PlanData, PlanOption } from '@/types'
import HeaderText from '@/views/NewSession/PlanVersions/HeaderText.vue'
import HeaderWrapper from '@/views/NewSession/PlanVersions/HeaderWrapper.vue'
import SelectPlanModalContent from '@/views/NewSession/PlanVersions/SelectPlanModalContent.vue'
import {
	formatLastUpdated,
	getPlanFullName,
} from '@/views/NewSession/PlanVersions/utils'
import { computed, onBeforeMount, type PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
	plan: {
		type: Object as PropType<Partial<PlanData>>,
		required: false,
	},
	index: {
		type: Number,
		required: true,
	},
	autoloadLatestPlan: {
		type: Boolean,
		require: true,
	},
	selectedPriceBook: {
		type: Object as PropType<PriceBook | undefined>,
		required: false,
	},
})

const versionHelper = usePlanVersionsV2()
const emit = defineEmits(['planSelected', 'createNewPlan'])
const { t } = useI18n()
const planStore = usePlanStore()
const cloudSyncService = useCloudSyncService()
const importedPlanOptions = ref<PlanOption[]>([])
const loading = ref<boolean>(true)
const showSelectionModal = ref(false)

const selectedPlan = defineModel('selectedPlan', {
	type: [String, null],
	required: false,
})

const selectedPlanOption = computed(() => {
	const importedPlan = importedPlanOptions.value.find(
		(option) => option.id === selectedPlan.value
	)

	return importedPlan ?? prototypeOption(props.plan!)
})

const handleSelectedPlanChange = async (planOption: PlanOption) => {
	showSelectionModal.value = false
	selectedPlan.value = planOption.id
	await emitPlanSelectionChange(planOption)
}

const retrievePlanBySource = async (
	planId: string,
	source: string
): Promise<Partial<PlanData> | undefined> => {
	if (source === 'cloud-sync') {
		const fetchedPlan = await cloudSyncService.plan.getBasicDataById(planId)
		if (fetchedPlan) {
			return fetchedPlan
		}
	} else if (source === 'local') {
		return await planStore.loadPlan(planId)
	}
	return undefined
}

const getPlanOptions = async () => {
	if (!props.plan) {
		return []
	}
	let planOptions: PlanOption[]

	const planVersions = await versionHelper.getRelatedPlanVersions(props.plan)

	planOptions = planVersions
		.filter((planVersion) =>
			versionHelper.isNewestVersion(planVersion, planVersions)
		)
		.map(versionHelper.mapPlanVersionToOption)

	// sort by date, filter out duplicate ids (when local and cloudSync plan have id collision, always take the newer plan)
	return planOptions.sort((a, b) => {
		return Date.parse(b.lastUpdated!) - Date.parse(a.lastUpdated!)
	})
}

// The function below is used to create a plan option for display if no imported options matched.
// this option will not trigger the emit flow
const prototypeOption = (plan: Partial<PlanData>): PlanOption => {
	return {
		id: props.plan?.id!,
		fullName: getPlanFullName(plan),
		lastUpdated: props.plan?.lastUpdated,
		formattedDate: formatLastUpdated(props.plan?.lastUpdated),
		source: 'local',
		funeralHomeId: props.plan?.funeralHomeId ?? '',
	}
}

onBeforeMount(async () => {
	importedPlanOptions.value = await getPlanOptions()
	// instead of the first one, select the first one that matches the local plan's name

	if (props.plan?.firstName && props.autoloadLatestPlan) {
		await loadLatestPlan()
	} else {
		selectedPlan.value = props.plan?.id
	}

	loading.value = false
})

async function loadLatestPlan() {
	// We're assuming the first match is latest because we order importedPlanOptions
	const initialValue = importedPlanOptions.value.find((option) =>
		option.fullName?.includes(props.plan!.firstName!)
	)
	// Not directly using selectedPlan ref in the emit because of some race conditions
	if (initialValue?.id) {
		await emitPlanSelectionChange(initialValue)
	}
	selectedPlan.value = initialValue?.id
}

const emitPlanSelectionChange = async (planOption: PlanOption) => {
	const plan = await retrievePlanBySource(
		planOption.id as string,
		planOption.source
	)
	emit('planSelected', {
		index: props.index,
		plan,
		source: planOption.source,
	})
}

const startNewPlan = () => {
	emit('createNewPlan', {
		index: props.index,
		plan: props.plan,
	})
}
</script>
